import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/pos`;

const UnitSettings = () => {
  const navigate = useNavigate();
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingUnit, setEditingUnit] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    short_name: '',
    allow_loose_sales: false,
    is_active: true
  });

  useEffect(() => {
    fetchUnits();
  }, []);

  const fetchUnits = async () => {
    try {
      const token = localStorage.getItem('pos_token');
      const response = await axios.get(`${API}/settings/units`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUnits(response.data);
    } catch (err) {
      setError('Failed to load units');
    } finally {
      setLoading(false);
    }
  };

  const seedDefaults = async () => {
    try {
      const token = localStorage.getItem('pos_token');
      await axios.post(`${API}/settings/units/seed-defaults`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccess('Default units added successfully!');
      fetchUnits();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to seed defaults');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.short_name) {
      setError('Name and short name are required');
      return;
    }

    setSaving(true);
    setError('');

    try {
      const token = localStorage.getItem('pos_token');

      if (editingUnit) {
        await axios.put(`${API}/settings/units/${editingUnit.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSuccess('Unit updated successfully!');
      } else {
        await axios.post(`${API}/settings/units`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSuccess('Unit added successfully!');
      }

      setShowModal(false);
      resetForm();
      fetchUnits();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save unit');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (unit) => {
    setEditingUnit(unit);
    setFormData({
      name: unit.name,
      short_name: unit.short_name,
      allow_loose_sales: unit.allow_loose_sales,
      is_active: unit.is_active
    });
    setShowModal(true);
  };

  const handleDelete = async (unitId) => {
    if (!window.confirm('Are you sure you want to delete this unit?')) return;

    try {
      const token = localStorage.getItem('pos_token');
      await axios.delete(`${API}/settings/units/${unitId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccess('Unit deleted successfully!');
      fetchUnits();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to delete unit');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      short_name: '',
      allow_loose_sales: false,
      is_active: true
    });
    setEditingUnit(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-600"></div>
      </div>
    );
  }

  return (
    <div data-testid="unit-settings-page" className="max-w-4xl mx-auto">
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => navigate('/gposp/settings')}
          className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
          data-testid="back-button"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-slate-800">Unit of Sale Settings</h1>
          <p className="text-slate-500">Configure measurement units and loose sales options</p>
        </div>
        <button
          onClick={() => { resetForm(); setShowModal(true); }}
          className="px-4 py-2 bg-gradient-to-r from-violet-600 to-purple-600 text-white rounded-xl font-medium hover:shadow-lg transition-all"
          data-testid="add-unit-btn"
        >
          + Add Unit
        </button>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700">
          {error}
          <button onClick={() => setError('')} className="ml-2 text-red-500">×</button>
        </div>
      )}

      {success && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-xl text-green-700">
          {success}
          <button onClick={() => setSuccess('')} className="ml-2 text-green-500">×</button>
        </div>
      )}

      {units.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
          <svg className="w-16 h-16 mx-auto mb-4 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
          </svg>
          <h3 className="text-lg font-semibold text-slate-800 mb-2">No Units Configured</h3>
          <p className="text-slate-500 mb-4">Add units of measurement for your products</p>
          <button
            onClick={seedDefaults}
            className="px-6 py-2 bg-violet-100 text-violet-700 rounded-xl hover:bg-violet-200 transition-colors"
            data-testid="seed-defaults-btn"
          >
            Add Default Units (pcs, kg, ltr, etc.)
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Unit Name</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Short Name</th>
                  <th className="px-4 py-3 text-center text-sm font-medium text-slate-600">Loose Sales</th>
                  <th className="px-4 py-3 text-center text-sm font-medium text-slate-600">Status</th>
                  <th className="px-4 py-3 text-center text-sm font-medium text-slate-600">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {units.map(unit => (
                  <tr key={unit.id} className="hover:bg-slate-50" data-testid={`unit-row-${unit.id}`}>
                    <td className="px-4 py-3 font-medium text-slate-800">{unit.name}</td>
                    <td className="px-4 py-3 text-slate-600">{unit.short_name}</td>
                    <td className="px-4 py-3 text-center">
                      {unit.allow_loose_sales ? (
                        <span className="px-2 py-1 bg-violet-100 text-violet-700 text-xs rounded-full">
                          Allowed
                        </span>
                      ) : (
                        <span className="px-2 py-1 bg-slate-100 text-slate-600 text-xs rounded-full">
                          Not Allowed
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {unit.is_active ? (
                        <span className="px-2 py-1 bg-emerald-100 text-emerald-700 text-xs rounded-full">Active</span>
                      ) : (
                        <span className="px-2 py-1 bg-slate-100 text-slate-600 text-xs rounded-full">Inactive</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => handleEdit(unit)}
                          className="p-1.5 text-violet-600 hover:bg-violet-50 rounded-lg transition-colors"
                          data-testid={`edit-unit-${unit.id}`}
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                          </svg>
                        </button>
                        <button
                          onClick={() => handleDelete(unit.id)}
                          className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          data-testid={`delete-unit-${unit.id}`}
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Info Card */}
      <div className="mt-6 bg-violet-50 border border-violet-200 rounded-xl p-4">
        <h3 className="font-semibold text-violet-800 mb-2">About Loose Sales</h3>
        <p className="text-sm text-violet-700">
          When "Loose Sales" is enabled for a unit, you can configure per-product quantity options:
        </p>
        <ul className="mt-2 text-sm text-violet-700 list-disc list-inside space-y-1">
          <li><strong>Fixed Options:</strong> Pre-defined quantities like 0.250, 0.500, 0.750 shown as buttons during billing</li>
          <li><strong>Editable:</strong> Free quantity input allowed during billing</li>
        </ul>
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
            <div className="p-4 border-b border-slate-200">
              <h3 className="text-lg font-semibold">{editingUnit ? 'Edit Unit' : 'Add Unit'}</h3>
            </div>
            <form onSubmit={handleSubmit} className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Unit Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Kilograms"
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
                  data-testid="unit-name-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Short Name *</label>
                <input
                  type="text"
                  value={formData.short_name}
                  onChange={(e) => setFormData({ ...formData, short_name: e.target.value })}
                  placeholder="e.g., kg"
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
                  data-testid="unit-short-name-input"
                />
                <p className="mt-1 text-xs text-slate-500">This will be displayed on bills</p>
              </div>
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="allow_loose"
                  checked={formData.allow_loose_sales}
                  onChange={(e) => setFormData({ ...formData, allow_loose_sales: e.target.checked })}
                  className="w-5 h-5 text-violet-600 rounded"
                  data-testid="allow-loose-checkbox"
                />
                <label htmlFor="allow_loose" className="cursor-pointer">
                  <p className="font-medium text-slate-800">Allow Loose Sales</p>
                  <p className="text-sm text-slate-500">Enable decimal quantities for products with this unit</p>
                </label>
              </div>
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  className="w-5 h-5 text-violet-600 rounded"
                  data-testid="is-active-checkbox"
                />
                <label htmlFor="is_active" className="cursor-pointer">
                  <p className="font-medium text-slate-800">Active</p>
                  <p className="text-sm text-slate-500">Show this unit in product forms</p>
                </label>
              </div>
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => { setShowModal(false); resetForm(); }}
                  className="flex-1 px-4 py-2 border border-slate-200 rounded-lg hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 bg-gradient-to-r from-violet-600 to-purple-600 text-white px-4 py-2 rounded-lg hover:shadow-lg disabled:opacity-50"
                  data-testid="save-unit-btn"
                >
                  {saving ? 'Saving...' : editingUnit ? 'Update' : 'Add'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default UnitSettings;
